import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { api } from "../../services/api";

type CohortUser = { group?: string } | undefined;

/**
 * Shared post-login navigation: shows the cohort form when the tenant has
 * selectable cohorts and the user hasn't picked one yet, otherwise goes
 * straight to the dashboard. Any failure fetching that data falls through
 * to the dashboard rather than stranding an already-authenticated user.
 */
export function useNavigateToDashboard(fetchUser: (userId: string) => Promise<CohortUser>) {
  const navigate = useNavigate();

  return async (
    shouldShowCohortForm: boolean,
    userId: string | undefined,
    onShowCohortForm: () => void
  ) => {
    if (shouldShowCohortForm && userId) {
      try {
        const [user, groupsResponse] = await Promise.all([
          fetchUser(userId),
          api.getOnboardingGroups(),
        ]);
        const hasSelectableCohorts = (groupsResponse?.data?.groups?.length ?? 0) > 0;
        if (!user?.group && hasSelectableCohorts) {
          onShowCohortForm();
          return;
        }
      } catch (error) {
        posthog.captureException(error, {
          properties: { name: "navigate_to_dashboard_cohort_check_error", userId },
        });
      }
    }
    navigate("/dashboard");
  };
}
